import { Component, OnInit, Signal, computed, inject } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ProductCartPreview } from "../shared/product-card/product-cart-preview/product-cart-preview.component";
import { SignalsStoreService } from '../shared/signals-store.service';
import { CarouselComponent } from '../shared/carousel/carousel.component';
import { OrderResponse, RELATED_ORDER_PRODUCTS } from '../shared/types/order.type';
import { OrderService } from '../shared/order.service';
import { ResolutionService } from '../shared/resolution.service';
import { CalculationsService } from '../shared/calculations.service';
import { PastDueBannerComponent } from '../shared/past-due-banner/past-due-banner.component';
import { AvailablePromotionsButtonComponent } from '../shared/promotions/available-promotions-button/available-promotions-button.component';

@Component({
  selector: 'app-cart-preview',
  templateUrl: './cart-preview.component.html',
  styleUrl: './cart-preview.component.scss',
  imports: [
    PastDueBannerComponent,
    RouterLink,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    FormsModule,
    CommonModule,
    ProductCartPreview,
    CarouselComponent,
    AvailablePromotionsButtonComponent
  ]
})
export class CartPreviewComponent implements OnInit {
  private signalsStoreService = inject(SignalsStoreService);
  private orderService = inject(OrderService);
  private resolutionService = inject(ResolutionService);
  private calculationsService = inject(CalculationsService);

  notYetSavedProducts: Signal<any> = computed(() => this.setUpProducts(false));
  inYourOrderProducts: Signal<any> = computed(() => this.setUpProducts(true));
  showCartPreview = this.signalsStoreService.showCartPreview;

  isContentLoaded: Signal<any> = computed(() => this.signalsStoreService.isContentLoaded());
  order: Signal<any | null> = computed(() => this.orderService.order());
  allProductsId: Signal<number[]> = computed(() => this.setUpProductsId());
  isMobile = computed(() => this.resolutionService.isMobile());

  subTotal: Signal<number> = computed(() => this.order()?.paymentDetails?.subTotal || 0);

  grossSubTotal: Signal<number> = computed(() => this.order()?.paymentDetails?.subtotalBeforeDiscounts || this.order()?.paymentDetails?.subTotal || 0);

  totalSavings: Signal<number> = computed(() => {
    return this.calculationsService.getOrderTotalSavings({
      subtotal: this.subTotal(),
      grossSubtotal: this.grossSubTotal(),
    })
  })

  carouselBuyAgainProducts: Signal<any | undefined> = computed(() => this.setUpCarouselItems(RELATED_ORDER_PRODUCTS.BUY_AGAIN, false));
  carouselFavoritesProducts: Signal<any | undefined> = computed(() => this.setUpCarouselItems(RELATED_ORDER_PRODUCTS.FAVORITES, false));
  carouselSuggestedProducts: Signal<any | undefined> = computed(() => this.setUpCarouselItems(RELATED_ORDER_PRODUCTS.SUGGESTED));

  isExplorerUser: Signal<boolean> = computed(() => this.signalsStoreService.isExplorerUser());

  ngOnInit(): void {
    if (this.isMobile())
      this.orderService.getOrder();
  }

  setUpProducts(confirmed: boolean) {
    const order = this.order();
    if (!order || !order?.products)
      return null

    if (!order?.products.common?.length && !order?.products.subscription?.length)
      return null

    return confirmed ? this.setUpConfirmedProducts(order) : this.setUpNoYetSavedProducts(order);
  }

  setUpConfirmedProducts(order: OrderResponse) {
    const commonProducts = order?.products.common?.filter((product: any) => product.isConfirmed) || [];
    const subscriptionProducts = order?.products.subscription?.filter((product: any) => product.isConfirmed) || [];
    if (!commonProducts.length && !subscriptionProducts.length)
      return null;

    return { common: (commonProducts || []).sort((a: any, b: any) => (b.updatedAt || 0) - (a.updatedAt || 0)), subscription: (subscriptionProducts || []).sort((a: any, b: any) => (b.updatedAt || 0) - (a.updatedAt || 0)) };
  }

  setUpNoYetSavedProducts(order: OrderResponse) {
    const commonProducts = order?.products.common?.filter((product: any) => !product.isConfirmed) || [];
    const subscriptionProducts = order?.products.subscription?.filter((product: any) => !product.isConfirmed) || [];
    if (!commonProducts.length && !subscriptionProducts.length)
      return null;

    return { common: (commonProducts || []).sort((a: any, b: any) => (b.updatedAt || 0) - (a.updatedAt || 0)), subscription: (subscriptionProducts || []).sort((a: any, b: any) => (b.updatedAt || 0) - (a.updatedAt || 0)) };
  }

  hideShowCartPreview() {
    this.showCartPreview.set(false);
  }


  private setUpProductsId(): any {
    const { common: odooCommonProducts = [], subscription: odooSubscriptionProducts = [] } = this.order()?.products || {};

    const commonProducts = [...odooCommonProducts];
    const subscriptionProducts = [...odooSubscriptionProducts];

    return [...commonProducts, ...subscriptionProducts].map((product: any) => product.id)
  }

  private setUpCarouselItems(orderProductType: string, isOrderSuggestedProduct: boolean = true) {
    const hiddenProducts: number[] = this.signalsStoreService.hiddenCarouselProducts();

    if (this.order()?.isSkipped || !this.isMobile()) return [];
    const selectedOrderProductType = Object.values(RELATED_ORDER_PRODUCTS).includes(orderProductType as RELATED_ORDER_PRODUCTS) ? orderProductType
      : RELATED_ORDER_PRODUCTS.SUGGESTED;

    if (!this.isContentLoaded || !this.order()?.relatedProducts?.[selectedOrderProductType] || !this.order()?.relatedProducts?.[selectedOrderProductType]?.length)
      if (!this.order()?.relatedProducts?.[selectedOrderProductType] || !this.order()?.relatedProducts?.[selectedOrderProductType]?.length)
        return []

    let items = this.order()?.relatedProducts?.[selectedOrderProductType];

    if (isOrderSuggestedProduct)
      items = items.filter((product: any) => !this.allProductsId().includes(product.id));

    if (hiddenProducts.length)
      items = items.filter((product: any) => hiddenProducts.indexOf(product.variantId) === -1)

    items = items.map((product: any) => {
      product.image = 'assets/images/logo-main.svg';
      product.name = product.name;
      if (typeof product?.price === 'number')
        product.price = product.price.toFixed(2);

      return ({
        content: {
          orderId: this.order()?.id,
          productDetails: product,
          settings: { isCardInOrderPage: true, hideWhenWasOOS: true }
        },
      })
    })

    return items
  }
}
